/**
 * 데스크톱 설치 앱의 탭 틀(작업 공간)의 순수 판단 — DOM 없음.
 * spec docs/superpowers/specs/2026-09-29-desktop-workspace-tabs-design.md
 * 틀(workspace-shell.js) · 탭 안(workspace-frame.js)이 함께 쓰고, 단위 시험은 tests/unit/workspace-logic.test.mjs.
 */

export const SHELL_PATH = '/workspace';
export const FRAME_PREFIX = 'ws-';
// 탭을 닫을 때 떠나기 가드에게 보내는 가짜 목적지 — 가드가 확인 뒤 navigate(이 주소, {force}) 로 이어 가면
// workspace-frame.js 가 라우터에서 받아 "닫아도 된다" 로 바꾼다 (§5.7).
export const CLOSE_SENTINEL = '/__ws_close__';
export const HISTORY_MAX = 100;
export const CLOSED_MAX = 20;
export const RESTORE_MAX = 50;

// 틀로 감싸지 않는 경로 — 로그인·연동 흐름과 공개 화면은 맨 위에 있어야 한다 (§6.2).
// 비밀번호 변경 · 인증 앱 등록은 보안 게이트가 /workspace 까지 되돌려 보내는 화면이라, 감싸면 돌고 돈다.
export const WRAP_EXCLUDE = ['/workspace', '/login', '/logout', '/offline', '/register', '/s', '/oauth2', '/saml', '/oidc', '/static', '/api', '/sw.js', '/manifest.json', '/password', '/totp/setup', '/totp/enable'];
// 같은 경로를 이만큼 안에 또 감싸려 하면 감싸지 않는다 — 서버가 되돌려 보내는 어떤 경우에도 돌지 않게.
export const WRAP_LOOP_MS = 10000;

// 틀 단축키 표 (§5.5). 틀 · 탭 안 · 브리지(server/apps_bridge.go 의 workspaceShellKeysJSON)가 같은 표를 쓴다 —
// 이 줄은 JSON 이어야 한다(server/workspace_test.go 가 읽어 브리지의 표와 견준다).
// 크롬의 탭 키(Ctrl+T/W/Tab · Ctrl+1–9)는 일반 브라우저 탭에서는 크롬이 가져가지만, 설치 앱 창에서는 페이지가 먼저
// 받는다(크롬은 앱 창의 키를 예약하지 않는다) — 틀은 설치 앱 창에만 있으므로 그대로 쓴다 (2026-09-29).
// 같은 동작의 키는 크롬과 같은 것을 앞에 둔다 — 도움말(helpRows)이 표 순서대로 보인다.
export const SHELL_KEYS = {"Ctrl+KeyT":"new-tab","Alt+KeyT":"new-tab","Ctrl+Shift+KeyT":"reopen-tab","Alt+Shift+KeyT":"reopen-tab","Ctrl+KeyW":"close-tab","Alt+KeyW":"close-tab","Ctrl+Digit1":"tab-1","Ctrl+Digit2":"tab-2","Ctrl+Digit3":"tab-3","Ctrl+Digit4":"tab-4","Ctrl+Digit5":"tab-5","Ctrl+Digit6":"tab-6","Ctrl+Digit7":"tab-7","Ctrl+Digit8":"tab-8","Ctrl+Digit9":"tab-last","Alt+Digit1":"tab-1","Alt+Digit2":"tab-2","Alt+Digit3":"tab-3","Alt+Digit4":"tab-4","Alt+Digit5":"tab-5","Alt+Digit6":"tab-6","Alt+Digit7":"tab-7","Alt+Digit8":"tab-8","Alt+Digit9":"tab-last","Ctrl+Tab":"next-tab","Ctrl+PageDown":"next-tab","Ctrl+Shift+Tab":"prev-tab","Ctrl+PageUp":"prev-tab","Alt+ArrowLeft":"back","Ctrl+BracketLeft":"back","Alt+ArrowRight":"forward","Ctrl+BracketRight":"forward","Ctrl+KeyL":"address","Alt+KeyD":"address","F6":"address","F5":"reload","Ctrl+KeyR":"reload","Ctrl+KeyP":"print","Ctrl+Slash":"help"};

// 단축키 도움말의 묶음과 순서. 'tab-n' 은 tab-1…tab-8 을 한 줄로 보인다.
export const HELP_LAYOUT = [
    ['tabs', ['new-tab', 'close-tab', 'reopen-tab', 'next-tab', 'prev-tab', 'tab-n', 'tab-last']],
    ['page', ['back', 'forward', 'reload', 'print']],
    ['other', ['address', 'help']],
];
// 표 밖의 도움말 키: ? 는 글 칸 밖에서만 — 브리지는 넘기지 않는다(서비스가 ? 를 쓴다), 탭 안은 페이지가 먼저 (helpKey).
const HELP_EXTRA = { help: ['?'] };
const KEY_NAMES = { ArrowLeft: '←', ArrowRight: '→', BracketLeft: '[', BracketRight: ']', PageDown: 'PgDn', PageUp: 'PgUp', Slash: '/' };

/** 표의 조합("Ctrl+Shift+KeyT")을 보이는 글자로. 맥은 ⌘ · ⌥ · ⇧ 를 붙여 쓰고, Tab · PgUp/PgDn 과 함께인 Ctrl 은
 *  ⌃ 다(⌘Tab 은 운영체제의 앱 전환이다). */
export function keyLabel(combo, mac) {
    const parts = String(combo || '').split('+');
    const code = parts.pop() || '';
    const key = KEY_NAMES[code] || code.replace(/^Key/, '').replace(/^Digit/, '');
    const ctrlIsControl = code === 'Tab' || code === 'PageDown' || code === 'PageUp';
    if (!mac) return parts.concat([key]).join('+');
    const sym = { Ctrl: ctrlIsControl ? '⌃' : '⌘', Alt: '⌥', Shift: '⇧' };
    return parts.map((m) => sym[m] || m).join('') + key;
}

/** 도움말의 줄: [{group, rows: [{action, keys: [보이는 글자…]}]}] — 표(SHELL_KEYS)에서 만든다. */
export function helpRows(mac) {
    const combosOf = (action) => Object.keys(SHELL_KEYS).filter((c) => SHELL_KEYS[c] === action);
    return HELP_LAYOUT.map(([group, actions]) => ({
        group,
        rows: actions.map((action) => {
            const keys = action === 'tab-n'
                ? combosOf('tab-1').map((c) => keyLabel(c, mac).replace(/1$/, '1–8'))
                : combosOf(action).map((c) => keyLabel(c, mac)).concat(HELP_EXTRA[action] || []);
            return { action, keys };
        }),
    }));
}

/** ? 로 도움말을 여는가 — 글 칸 밖, 조합 중이 아닐 때, Ctrl · Alt · ⌘ 없이. */
export function helpKey(e, editable) {
    if (!e || e.isComposing || editable) return false;
    return e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey;
}

export function shouldDrawShell({ standalone, phone }) {
    return !!standalone && !phone;
}

/** 폰의 User-Agent — 서버 workspacePhoneUA(server/workspace.go)와 같은 규칙. 서버가 /workspace 를 틀 없이
 *  돌려보내는 창이므로, 화면 폭이 넓어도(펼친 폴드 · 가로 폰) 감싸지 않는다. */
export function phoneUA(ua) {
    const s = String(ua || '');
    return s.includes('iPhone') || (s.includes('Android') && s.includes('Mobile'));
}

/** 방금(WRAP_LOOP_MS 안에) 같은 경로를 감쌌는가 — mark 는 sessionStorage 에 둔 {path, t}. */
export function wrapLooping(mark, path, now) {
    return !!mark && mark.path === path && typeof mark.t === 'number' && now - mark.t >= 0 && now - mark.t < WRAP_LOOP_MS;
}

function underPrefix(pathname, prefix) {
    return pathname === prefix || pathname.startsWith(prefix + '/');
}

/** 틀 밖의 설치 앱 창이 옮겨 갈 주소, 감싸지 않을 경로면 null (§6.2). */
export function wrapTarget(pathname, search = '', hash = '') {
    const p = pathname || '/';
    if (WRAP_EXCLUDE.some((ex) => underPrefix(p, ex))) return null;
    return SHELL_PATH + '?open=' + encodeURIComponent(p + (search || '') + (hash || ''));
}

/** ?open= — 같은 오리진 경로만, 틀 자신은 아니다. 아니면 ''.
 *  제어 문자는 거절한다: URL 파서가 탭 · 줄바꿈을 지워 "/\t/evil.com" 을 //evil.com 으로 읽는다. */
export function safeOpen(raw) {
    const s = typeof raw === 'string' ? raw.trim() : '';
    if (!s.startsWith('/') || s.startsWith('//') || s.includes('\\') || /[\x00-\x1f\x7f]/.test(s)) return '';
    const base = 'http://ws.invalid';
    const u = tryURL(s, base);
    if (!u || u.origin !== base) return '';
    if (underPrefix(u.pathname, SHELL_PATH)) return '';
    return s;
}

/**
 * 탭 안의 링크 클릭 (§5.2). kind:
 *   default  — 가로채지 않는다 (페이지가 하던 대로)
 *   block    — 막는다 (틀 자신으로 가는 링크)
 *   current  — 같은 탭 (_top/_parent 가 틀을 떠나지 않게)
 *   tab      — GROUPCREW 새 탭, mode background|foreground
 *   service  — 사내 서비스 새 탭, mode · service
 *   external — 시스템 브라우저
 */
export function classifyLink({ url, origin, target = '', button = 0, ctrl = false, meta = false, shift = false, download = false, services = [] }) {
    if (!url || download) return { kind: 'default' };
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return { kind: 'default' };
    const t = String(target || '').toLowerCase();
    const escapes = t === '_top' || t === '_parent';
    const named = !!t && !escapes && t !== '_self';
    const background = (ctrl || meta || button === 1) && !shift;
    const wantNew = ctrl || meta || shift || button === 1 || named;
    const mode = background ? 'background' : 'foreground';
    if (url.origin === origin) {
        if (underPrefix(url.pathname, SHELL_PATH)) return { kind: 'block' };
        if (wantNew) return { kind: 'tab', mode };
        if (escapes) return { kind: 'current' };
        return { kind: 'default' };
    }
    const service = (services || []).find((s) => s && s.origin === url.origin);
    if (service && service.mode !== 'tab') return { kind: 'service', mode, service };
    return { kind: 'external' };
}

function tryURL(s, base) {
    try { return base ? new URL(s, base) : new URL(s); } catch (_) { return null; }
}

/** 주소 칸 (§5.4). */
export function parseAddress(value, { origin, aliasHosts = [], searchOn = false } = {}) {
    const v = String(value == null ? '' : value).trim();
    if (!v) return { kind: 'invalid' };
    let u = null;
    if (v.startsWith('/') && !v.startsWith('//')) u = tryURL(v, origin);
    else if (/^[a-z][a-z0-9+.-]*:\/\//i.test(v)) u = tryURL(v);
    else if (/^localhost(:\d+)?([/?#]|$)/i.test(v)) u = tryURL('http://' + v);
    else if (!/\s/.test(v) && /^[\w-]+(\.[\w-]+)+(:\d+)?([/?#]|$)/.test(v)) u = tryURL('https://' + v);
    if (u) {
        if (u.protocol !== 'http:' && u.protocol !== 'https:') return { kind: 'invalid' };
        if (u.origin !== origin && aliasHosts.includes(u.host)) u = tryURL(u.pathname + u.search + u.hash, origin);
        return u ? { kind: 'url', url: u } : { kind: 'invalid' };
    }
    if (searchOn) return { kind: 'search', url: tryURL('/search?q=' + encodeURIComponent(v), origin) };
    return { kind: 'invalid' };
}

export function comboOf(e) {
    const parts = [];
    if (e.ctrlKey || e.metaKey) parts.push('Ctrl');
    if (e.altKey) parts.push('Alt');
    if (e.shiftKey) parts.push('Shift');
    parts.push(e.code || e.key || '');
    return parts.join('+');
}

/** 틀 단축키의 동작 이름. ctx.mac · ctx.editable: 맥의 글 칸에서는 Option 조합(단어 이동 ←/→ · 특수 문자 입력)을
 *  글 칸에 둔다. */
export function shellKey(e, ctx = {}) {
    if (!e || e.isComposing) return null;
    if (ctx.mac && ctx.editable && e.altKey && !e.ctrlKey && !e.metaKey) return null;
    return SHELL_KEYS[comboOf(e)] || null;
}

export function isMacPlatform(nav) {
    const n = nav || (typeof navigator !== 'undefined' ? navigator : {});
    const p = (n.userAgentData && n.userAgentData.platform) || n.platform || '';
    return /mac|iphone|ipad/i.test(String(p));
}

/** 키 입력을 받는 요소인가 (글 칸 · 편집 영역). */
export function isEditableTarget(el) {
    if (!el || el.nodeType !== 1) return false;
    if (el.isContentEditable) return true;
    const tag = String(el.tagName || '').toUpperCase();
    if (tag === 'TEXTAREA') return true;
    if (tag !== 'INPUT') return false;
    const type = String(el.type || 'text').toLowerCase();
    return !['button', 'checkbox', 'radio', 'submit', 'reset', 'file', 'image', 'range', 'color', 'hidden'].includes(type);
}

/**
 * 탭 하나의 뒤로가기 기록 (§5.3). 틀이 시작한 이동(arm · go · step)은 도착 보고를 기다리고(pending),
 * 탭이 떠나기 확인에서 머물면 blocked() 로 되돌린다.
 */
export class TabHistory {
    constructor(url) {
        this.stack = [url];
        this.idx = 0;
        this.pending = null;
    }

    get url() { return this.stack[this.idx]; }
    get canBack() { return this.idx > 0; }
    get canForward() { return this.idx < this.stack.length - 1; }

    /** 문서를 새로 불러올 때(처음 · 새로고침): 다음 보고를 지금 칸에 받는다 — 리다이렉트로 닿은 주소도. */
    arm() {
        this.pending = { kind: 'load', idx: this.idx, url: this.url, from: this.idx, blocked: false };
    }

    /** 주소 칸 · 같은 탭 이동: 새 칸을 쌓고 도착을 기다린다. */
    go(url) {
        const prev = { stack: this.stack.slice(), idx: this.idx };
        const drop = this.#superseded();
        this.#push(url);
        this.pending = { kind: 'go', idx: this.idx, url, from: prev.idx, prev, blocked: false, drop };
        return url;
    }

    /** ← → : 칸을 옮기고 도착을 기다린다. 갈 곳이 없으면 null. */
    step(d) {
        const to = this.idx + d;
        if (to < 0 || to >= this.stack.length) return null;
        const drop = this.#superseded();
        this.pending = { kind: 'step', idx: to, url: this.stack[to], from: this.idx, blocked: false, drop };
        this.idx = to;
        return this.url;
    }

    // 아직 닿지 않은 앞 이동이 있으면 그 목적지들 — 버려진 이동의 뒤늦은 보고가 새 칸을 덮지 않게.
    #superseded() {
        const p = this.pending;
        if (!p || p.blocked || p.kind === 'load') return [];
        return [p.url].concat(p.drop || []).slice(0, 8);
    }

    /** 탭이 떠나기 확인에서 머물렀다. */
    blocked() {
        const p = this.pending;
        if (!p || p.blocked) return;
        if (p.kind === 'go') { this.stack = p.prev.stack; this.idx = p.prev.idx; } else { this.idx = p.from; }
        p.blocked = true;
    }

    /** 탭이 알린 주소. how: 'load' | 'push' | 'replace'. 기록이 바뀌면 true. */
    report(url, how = 'push') {
        const p = this.pending;
        if (p && !p.blocked) {
            // 떠나기 직전의 칸을 고치는 replaceState(라우터의 markCurrentEntry, _from 지우기)는 목적지가 아니다.
            if (how === 'replace' && url !== p.url && p.kind !== 'load') return false;
            // 앞질린 이동의 문서가 늦게 알린 주소 — 지금 기다리는 칸의 도착이 아니다.
            if (url !== p.url && (p.drop || []).includes(url)) return false;
            this.pending = null;
            const changed = this.idx !== p.idx || this.stack[p.idx] !== url;
            this.idx = p.idx;
            this.stack[this.idx] = url;
            return changed;
        }
        if (p && p.blocked) {
            // 머문 탭이 확인 뒤 이어 가기 직전에 지금 칸을 고치는 replaceState(라우터의 markCurrentEntry) —
            // 지금 칸만 고치고 기다림은 둔다. 그러지 않으면 이어 가는 ← → 가 새 칸으로 쌓인다.
            if (how === 'replace' && url !== p.url) {
                if (this.stack[this.idx] === url) return false;
                this.stack[this.idx] = url;
                return true;
            }
            this.pending = null;
            // 가드가 확인 뒤 이어 간 ← → 는 그 칸으로. go 는 되돌린 기록 위의 새 칸으로(아래).
            if (url === p.url && p.kind === 'step') { this.idx = p.idx; return true; }
        }
        if (how === 'replace') {
            if (this.stack[this.idx] === url) return false;
            this.stack[this.idx] = url;
            return true;
        }
        if (url === this.stack[this.idx]) return false;
        this.#push(url);
        return true;
    }

    #push(url) {
        this.stack.splice(this.idx + 1);
        this.stack.push(url);
        if (this.stack.length > HISTORY_MAX) this.stack.splice(0, this.stack.length - HISTORY_MAX);
        this.idx = this.stack.length - 1;
    }
}

/** 지금 탭에서 연달아 연 n 번째 새 탭의 자리 (크롬처럼 지금 탭 바로 뒤, 연 순서대로). */
export function openerIndex(activeIndex, n, total) {
    return Math.min(activeIndex + n, total);
}

export function serializeTabs(list, activeIndex) {
    return {
        v: 1,
        active: Math.max(0, activeIndex),
        tabs: (list || []).map((t) => ({ url: t.url, title: t.title || '', svc: t.svcId || '' })),
    };
}

export function restorePlan(saved) {
    if (!saved || saved.v !== 1 || !Array.isArray(saved.tabs)) return { tabs: [], active: 0 };
    const tabs = saved.tabs
        .filter((t) => t && typeof t.url === 'string' && t.url)
        .slice(0, RESTORE_MAX)
        .map((t) => ({ url: t.url, title: String(t.title || ''), svc: String(t.svc || '') }));
    const a = saved.active;
    const active = Number.isInteger(a) && a >= 0 && a < tabs.length ? a : 0;
    return { tabs, active };
}

/** 문서 제목에서 앱 이름 꼬리(`… · GROUPCREW`)를 뗀다. */
export function cleanTitle(title, suffix) {
    let s = String(title || '').replace(/\s+/g, ' ').trim();
    const tail = suffix ? ' · ' + suffix : '';
    if (tail && s.endsWith(tail)) s = s.slice(0, -tail.length).trim();
    return s;
}

export function displayURL(raw) {
    const u = tryURL(String(raw || ''));
    if (!u) return String(raw || '');
    const rest = u.pathname === '/' && !u.search && !u.hash ? '' : u.pathname + u.search + u.hash;
    return u.host + rest;
}

/** 사이드바 항목의 경로 접두 표(`[{p, i}]`)에서 가장 긴 접두의 아이콘. */
export function iconFor(pathname, table) {
    let best = null;
    for (const row of table || []) {
        const p = row && row.p;
        if (!p || p === '/') continue;
        if (underPrefix(pathname, p) && (!best || p.length > best.p.length)) best = row;
    }
    return best ? best.i : '';
}

export function isLoginPath(raw) {
    const u = tryURL(String(raw || ''));
    return !!u && u.pathname === '/login';
}
